import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import nb from './locales/nb/translation.json'
import en from './locales/en/translation.json'
import { DEFAULT_LANGUAGE, STORAGE_KEYS, SUPPORTED_LANGUAGES } from '@/lib/constants'
import { retireLegacyLanguageCache } from '@/lib/language'

// Before init, so the detector cannot read a sniffed value out of the old key
// on this very boot. See lib/language.ts.
retireLegacyLanguageCache()

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      nb: { translation: nb },
      en: { translation: en },
    },
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    interpolation: {
      escapeValue: false,
    },
    /**
     * N-13. `navigator` is still consulted, but it is now the THIRD voice, not
     * the first: an explicit choice sits above it in `lookupLocalStorage`, and
     * an event's declared language sits between them (`useEventLanguage`).
     *
     * `caches: []` is load-bearing, not tidiness. With caching on, i18next
     * wrote whatever it sniffed off `navigator` into the lookup key during
     * init, so the key answered "what did the browser say once" while every
     * reader treated it as "what did the visitor choose". Nothing writes it
     * now except `setLanguageChoice`, which is a person pressing something.
     */
    //
    // `?lang=nb|en` comes first (30.10.2026): it is the address of one language, for the sitemap's
    // hreflang links and for crawlers, which have no stored choice and one browser language. It is
    // read, never stored (`caches: []`), so it lasts for the visit it opened and changes no choice.
    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: STORAGE_KEYS.LANGUAGE,
      caches: [],
    },
    // Silence the Locize sponsorship banner i18next prints in dev/prod
    // consoles. https://github.com/i18next/i18next/issues/2010
    debug: false,
    saveMissing: false,
  })

// Suppress the Locize sponsorship console banner that i18next prints on
// startup. It's a vendor promo, not actionable, and clutters the console.
i18n.options.partialBundledLanguages = true
const noop = () => {}
const origLog = console.log
console.log = function patchedLog(...args: unknown[]) {
  if (
    typeof args[0] === 'string' &&
    (args[0].includes('i18next is maintained') || args[0].includes('locize.com'))
  ) {
    return noop()
  }
  return origLog.apply(console, args)
}

export default i18n
